import React from 'react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { useTheme } from '../../../hooks/useTheme';

type ValueFormat = 'auto' | 'percent' | 'currency';
type ResolvedValueFormat = 'percent' | 'currency' | 'number';

interface LineChartDataPoint {
  date?: string;
  name?: string;
  value?: number;
  incomplete?: boolean;
}

interface LineChartProps {
  data: LineChartDataPoint[];
  title: string;
  loading: boolean;
  valueFormat?: ValueFormat;
  showIncompleteZone?: boolean;
}

const INCOMPLETE_TOOLTIP =
  'Período incompleto — los CyDs pueden seguir llegando';

const LineChart: React.FC<LineChartProps> = ({
  data,
  title,
  loading,
  valueFormat = 'auto',
  showIncompleteZone = false,
}) => {
  const theme = useTheme();

  if (loading || !data || data.length === 0) {
    return <div>Sin datos</div>;
  }

  const resolvedFormat: ResolvedValueFormat =
    valueFormat === 'percent'
      ? 'percent'
      : valueFormat === 'currency'
        ? 'currency'
        : title.toLowerCase().includes('venta')
          ? 'currency'
          : 'number';

  const transformedData = data
    .map((item) => ({
      name: String(item.date || item.name || ''),
      value: typeof item.value === 'number' ? item.value : Number(item.value) || 0,
      incomplete: Boolean(item.incomplete),
    }))
    .filter((item) => !Number.isNaN(item.value) && Number.isFinite(item.value) && item.value >= 0);

  if (transformedData.length === 0) {
    return <div>Sin datos válidos</div>;
  }

  const sanitizedData = transformedData.map((item) => ({
    ...item,
    value:
      resolvedFormat === 'percent'
        ? Math.round(item.value * 100) / 100
        : Math.max(0.01, Math.round(item.value * 100) / 100),
  }));

  const values = sanitizedData.map((item) => item.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  const domain =
    resolvedFormat === 'percent'
      ? [0, Math.max(maxValue * 1.15, 1)]
      : [Math.max(0, Math.floor(minValue * 0.9)), Math.max(maxValue * 1.1, 1)];

  const formatCurrencyAxis = (value: number) => {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000) {
      const formatted = (abs / 1_000_000).toFixed(1).replace(/\.0$/, '');
      return `${sign}$${formatted}M`;
    }
    if (abs >= 1_000) {
      const formatted = (abs / 1_000).toFixed(1).replace(/\.0$/, '');
      return `${sign}$${formatted}K`;
    }
    return `${sign}$${Math.round(abs)}`;
  };

  const formatNumberAxis = (value: number) => {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000) {
      const formatted = (abs / 1_000_000).toFixed(1).replace(/\.0$/, '');
      return `${sign}${formatted}M`;
    }
    if (abs >= 1_000) {
      const formatted = (abs / 1_000).toFixed(1).replace(/\.0$/, '');
      return `${sign}${formatted}K`;
    }
    return `${sign}${Math.round(abs)}`;
  };

  const formatPercentAxis = (value: number) => {
    const rounded = Math.round(value * 100) / 100;
    return `${rounded.toLocaleString('es-AR', { maximumFractionDigits: 2 })}%`;
  };

  const formatAxis =
    resolvedFormat === 'percent'
      ? formatPercentAxis
      : resolvedFormat === 'currency'
        ? formatCurrencyAxis
        : formatNumberAxis;

  const formatDateTick = (value: string) => {
    if (!value) return value;
    const calendarMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (calendarMatch) {
      const [, year, month, day] = calendarMatch;
      return `${day}/${month}/${year.slice(-2)}`;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const firstIncompleteIndex = showIncompleteZone
    ? sanitizedData.findIndex((item) => item.incomplete)
    : -1;
  const hasIncompleteZone = firstIncompleteIndex >= 0;

  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{ payload?: { value?: number; incomplete?: boolean } }>;
    label?: string;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }

    const point = payload[0]?.payload;
    const value = Number(point?.value ?? 0);

    return (
      <div
        style={{
          backgroundColor: theme.colors.background.card,
          border: `1px solid ${theme.colors.text.muted}`,
          borderRadius: theme.radius.md,
          boxShadow: theme.shadows.md,
          padding: '8px 10px',
          maxWidth: 240,
        }}
      >
        <div style={{ color: theme.colors.text.primary, fontWeight: 600, marginBottom: 4 }}>
          {formatDateTick(String(label ?? ''))}
        </div>
        <div style={{ color: theme.colors.text.secondary }}>{formatAxis(value)}</div>
        {point?.incomplete && (
          <div style={{ color: theme.colors.text.muted, fontSize: 11, marginTop: 6, lineHeight: 1.35 }}>
            {INCOMPLETE_TOOLTIP}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ width: '100%', height: '300px' }}>
      <ResponsiveContainer>
        <RechartsLineChart data={sanitizedData}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={theme.colors.text.muted}
            strokeOpacity={0.3}
          />
          {hasIncompleteZone && (
            <ReferenceArea
              x1={sanitizedData[firstIncompleteIndex].name}
              x2={sanitizedData[sanitizedData.length - 1].name}
              fill={theme.colors.chart.primary}
              fillOpacity={0.08}
              strokeOpacity={0}
            />
          )}
          <XAxis
            dataKey="name"
            tick={{ fill: theme.colors.text.secondary, fontSize: 12 }}
            axisLine={{ stroke: theme.colors.text.muted }}
            tickLine={{ stroke: theme.colors.text.muted }}
            allowDataOverflow={false}
            scale="point"
            tickFormatter={formatDateTick}
          />
          <YAxis
            domain={domain}
            tick={{ fill: theme.colors.text.secondary, fontSize: 12 }}
            axisLine={{ stroke: theme.colors.text.muted }}
            tickLine={{ stroke: theme.colors.text.muted }}
            allowDataOverflow={false}
            scale="linear"
            tickFormatter={formatAxis}
          />
          <Tooltip content={renderTooltip} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={theme.colors.chart.primary}
            strokeWidth={2}
            dot={(props) => {
              const { cx, cy, payload } = props as {
                cx?: number;
                cy?: number;
                payload?: { incomplete?: boolean };
              };
              if (cx == null || cy == null) {
                return <circle cx={0} cy={0} r={0} fill="transparent" stroke="transparent" />;
              }
              const fill = payload?.incomplete
                ? theme.colors.text.muted
                : theme.colors.chart.primary;
              return <circle cx={cx} cy={cy} r={4} fill={fill} stroke={fill} strokeWidth={2} />;
            }}
            activeDot={{ r: 6, stroke: theme.colors.chart.primary, strokeWidth: 2 }}
            connectNulls={false}
            isAnimationActive={false}
            strokeDasharray=""
          />
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default LineChart;
